/**
 * MemberForm — formulaire de création/édition d'un membre dans une modale.
 */
import { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import Input from "./Input";
import Select from "./Select";
import Button from "./Button";
import { colors } from "../styles/tokens";
import { cellulesApi } from "../services/api";

const STATUTS = [
  { value: "nouveau",  label: "Nouveau" },
  { value: "actif",    label: "Actif" },
  { value: "inactif",  label: "Inactif" },
  { value: "visiteur", label: "Visiteur" },
];

const GENRES = [
  { value: "homme", label: "Homme" },
  { value: "femme", label: "Femme" },
];

const SITUATIONS = [
  { value: "celibataire", label: "Célibataire" },
  { value: "marie",       label: "Marié(e)" },
  { value: "divorce",     label: "Divorcé(e)" },
  { value: "veuf",        label: "Veuf / Veuve" },
];

const EMPTY = {
  prenom: "", nom: "", email: "", telephone: "",
  eglise_id: "", role_eglise: "", departement: "", profession: "",
  cellule_id: "", statut: "nouveau", genre: "", situation_familiale: "",
  ville: "", pays: "",
  date_naissance: "", date_arrivee_eglise: "",
};

export default function MemberForm({
  open,
  onClose,
  onSubmit,
  eglises = [],
  initial = null,           // si fourni, mode édition
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [cellules, setCellules] = useState([]);

  // Reset / pré-remplit le formulaire à chaque ouverture
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        ...EMPTY,
        ...initial,
        eglise_id: initial.eglise_id ?? "",
        cellule_id: initial.cellule_id ? String(initial.cellule_id) : "",
        date_naissance: initial.date_naissance || "",
        date_arrivee_eglise: initial.date_arrivee_eglise || "",
      });
    } else {
      // Pas de pré-sélection : l'utilisateur doit choisir l'église explicitement
      setForm({ ...EMPTY });
    }
    setErrors({});
    setGlobalError(null);
  }, [open, initial, eglises]);

  // Charge la liste des cellules à l'ouverture
  useEffect(() => {
    if (!open) return;
    cellulesApi.list()
      .then(({ data }) => setCellules(data || []))
      .catch(() => setCellules([]));
  }, [open]);

  /** Cellules de l'église sélectionnée (filtrage live). */
  const cellulesOptions = useMemo(() => {
    if (!form.eglise_id) return [];
    return cellules
      .filter((c) => c.eglise_id === Number(form.eglise_id))
      .map((c) => ({ value: String(c.id), label: c.nom }));
  }, [cellules, form.eglise_id]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    if (key === "eglise_id") {
      // Changement d'église → on retire la cellule (qui appartenait à l'autre église)
      setForm((prev) => ({ ...prev, eglise_id: value, cellule_id: "" }));
    } else {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  /** Validation côté client. */
  const validate = () => {
    const errs = {};
    if (!form.prenom.trim()) errs.prenom = "Prénom requis";
    if (!form.nom.trim())    errs.nom = "Nom requis";
    if (!form.eglise_id)     errs.eglise_id = "Veuillez sélectionner une église";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Email invalide";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      // Nettoyage : retire les chaînes vides + coerce les FK en nombres
      const payload = Object.fromEntries(
        Object.entries(form)
          .filter(([_, v]) => v !== "" && v !== null && v !== undefined)
          .map(([k, v]) => {
            if (k === "eglise_id" || k === "cellule_id") return [k, Number(v)];
            return [k, v];
          })
      );

      const result = await onSubmit(payload);
      if (result?.ok === false) {
        setGlobalError(result.error || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      setGlobalError(err.message || "Erreur inattendue");
    } finally {
      setSubmitting(false);
    }
  };

  const eglisesOptions = eglises.map((e) => ({
    value: String(e.id),
    label: `${e.nom}${e.ville ? ` — ${e.ville}` : ""}`,
  }));

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={initial ? "Modifier le membre" : "Nouveau membre"}
      subtitle={initial ? `Modification de ${initial.prenom} ${initial.nom}` : "Ajoutez un fidèle à votre église"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {initial ? "Enregistrer" : "Créer le membre"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
          }}>
            {globalError}
          </div>
        )}

        {/* Identité */}
        <Section title="Identité">
          <Grid>
            <Input label="Prénom" value={form.prenom} onChange={set("prenom")} required error={errors.prenom} />
            <Input label="Nom"    value={form.nom}    onChange={set("nom")}    required error={errors.nom} />
            <Select label="Genre" value={form.genre} onChange={set("genre")} options={GENRES} placeholder="Non renseigné" />
            <Input label="Date de naissance" type="date" value={form.date_naissance} onChange={set("date_naissance")} />
          </Grid>
        </Section>

        {/* Contact */}
        <Section title="Contact">
          <Grid>
            <Input label="Email"     type="email" value={form.email}     onChange={set("email")}     icon="✉" error={errors.email} />
            <Input label="Téléphone"              value={form.telephone} onChange={set("telephone")} icon="📞" />
            <Input label="Ville" value={form.ville} onChange={set("ville")} />
            <Input label="Pays"  value={form.pays}  onChange={set("pays")} />
          </Grid>
        </Section>

        {/* Engagement ecclésial */}
        <Section title="Engagement">
          <Grid>
            <Select
              label="Église" value={form.eglise_id} onChange={set("eglise_id")}
              options={eglisesOptions} required error={errors.eglise_id}
              placeholder="Sélectionnez une église..."
            />
            <Select label="Statut" value={form.statut} onChange={set("statut")} options={STATUTS} placeholder={null} />
            <Input  label="Rôle dans l'église"   value={form.role_eglise} onChange={set("role_eglise")} placeholder="Diacre, Ancien…" />
            <Input  label="Département"          value={form.departement} onChange={set("departement")} placeholder="Louange, Jeunesse…" />
            <Select
              label="Cellule"
              value={form.cellule_id}
              onChange={set("cellule_id")}
              options={cellulesOptions}
              placeholder={
                !form.eglise_id ? "Sélectionnez d'abord une église"
                : cellulesOptions.length === 0 ? "Aucune cellule pour cette église"
                : "Aucune"
              }
              disabled={!form.eglise_id || cellulesOptions.length === 0}
            />
            <Input  label="Date d'adhésion"      type="date" value={form.date_arrivee_eglise} onChange={set("date_arrivee_eglise")} />
          </Grid>
        </Section>

        {/* Profil */}
        <Section title="Profil">
          <Grid>
            <Input  label="Profession" value={form.profession} onChange={set("profession")} />
            <Select label="Situation familiale" value={form.situation_familiale} onChange={set("situation_familiale")} options={SITUATIONS} placeholder="Non renseigné" />
          </Grid>
        </Section>

        {/* Submit caché : permet la touche Entrée */}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{
        fontSize: "11px",
        fontWeight: 700,
        color: colors.gold,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        marginBottom: "10px",
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "12px",
    }}>
      {children}
    </div>
  );
}
